import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { login, setToken, setUser, isAuthenticated, clearToken, clearUser } from '@/lib/api';
import { ShieldCheck, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated()) {
      navigate('/admin');
      return;
    }
    // Show expired token message — accept either ?expired=1 (legacy) or
    // the more specific ?r=preempt|expired|stale flags. All tell the
    // user the same story: please sign in again.
    const params = new URLSearchParams(location.search);
    if (params.get('expired') === '1') {
      // Clean up any stale token/user from localStorage (idempotent if
      // already cleared by the api interceptor).
      clearToken();
      clearUser();
      const reason = params.get('r') || '';
      const msg =
        reason === 'preempt'
          ? 'Your session has timed out. Please sign in again.'
          : 'Your session has expired. Please sign in again.';
      toast.error(msg);
    }
  }, [navigate, location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill in all fields'); return; }
    setLoading(true);
    try {
      const resp = await login(email, password);
      setToken(resp.data.token);
      setUser(resp.data.user);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4 relative noise-bg">
      <Card className="max-w-sm w-full rounded-2xl shadow-[var(--shadow-lift)]">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="h-7 w-7 text-[var(--brand-ocean-700)]" />
            <span className="font-heading font-bold text-xl text-[var(--brand-ocean-700)]">ImpactQuote</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-[var(--brand-slate-900)] mb-1">Admin Login</h1>
          <p className="text-sm text-[var(--brand-slate-600)] mb-6">Sign in to manage leads and quotes</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="admin-login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@impactquote.com" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" data-testid="admin-login-password-input" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" className="mt-1.5 pr-10" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-[var(--brand-slate-600)]" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-600)] text-white rounded-xl h-11 font-semibold transition-colors duration-200" data-testid="admin-login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
